import { defineStore } from 'pinia';
import {
	type SelfServiceAddon,
	type SelfServiceCompanyAccount,
	type SelfServiceData,
	type SelfServiceFeatureOverLimit,
	type SelfServiceOnboardingPackage,
	type SelfServicePackage,
} from '@/generated/graphql';

/**
 * We put in this store self service data that is possible
 * to be updated by the user. We then track this data to
 * query updated cost for intermittent configuration
 */

export type SelfServiceCompanyAccountWithFormFields =
	Partial<SelfServiceCompanyAccount> & {
		isEditMode?: boolean;
	};

export type SelfServiceStoreType = Partial<
	Pick<
		SelfServiceData,
		| 'billingCycle'
		| 'paidSeatCount'
		| 'addons'
		| 'customAddons'
		| 'standardPaymentSum'
		| 'standardPaymentDate'
	> & {
		currentPackageId: SelfServicePackage['id'];
		activePackageId: SelfServicePackage['id'];
		currentOnboardingPackageId: SelfServiceOnboardingPackage['id'];
		companyAccounts: SelfServiceCompanyAccountWithFormFields[];

		isInitialised: boolean;
		hasChanged: boolean;
		isSaving: boolean;
		userChangeAddons: SelfServiceAddon[];
		userChangeCustomAddons: SelfServiceAddon[];
		showAddonsModal: boolean;
		showSaveChangesConfirmationModal: boolean;
		showSetupPaymentModal: boolean;
		isCostLoading: boolean;
		isAddonsLoading: boolean;
		isAddonsChanged: boolean;
		showAnnualBillingConfirmationModal: boolean;
		isVoucherApplied: boolean;
		featuresOverLimitWithHardLimit: SelfServiceFeatureOverLimit[];
		featuresOverLimitWithSoftLimit: SelfServiceFeatureOverLimit[];
		featuresOverLimitWillDeleteData: SelfServiceFeatureOverLimit[];
		contactSalesFormData: Record<string, string>;
	}
>;

export const useSelfServiceFormStore = defineStore('selfServiceForm', {
	state: (): SelfServiceStoreType => {
		return {
			isInitialised: false,
			hasChanged: false,
			isSaving: false,
			userChangeAddons: undefined,
			userChangeCustomAddons: undefined,
			showAddonsModal: false,
			showSaveChangesConfirmationModal: false,
			showSetupPaymentModal: false,
			isCostLoading: false,
			isAddonsLoading: false,
			isAddonsChanged: false,
			standardPaymentSum: undefined,
			standardPaymentDate: undefined,
			showAnnualBillingConfirmationModal: false,
			isVoucherApplied: false,

			// User form data
			billingCycle: undefined,
			paidSeatCount: undefined,
			currentPackageId: undefined,
			activePackageId: undefined,
			currentOnboardingPackageId: undefined,
			companyAccounts: undefined,
			addons: undefined,
			customAddons: undefined,
			featuresOverLimitWithHardLimit: undefined,
			featuresOverLimitWithSoftLimit: undefined,
			featuresOverLimitWillDeleteData: undefined,
			contactSalesFormData: {},
		};
	},
});
